// Danh mục module tính năng — nguồn sự thật cho menu (web), bật/tắt theo tenant (identity) và
// chặn route của module đang tắt (gateway). Thêm module mới: thêm một dòng ở đây + khoá quyền trong permissions.ts.
import type { Grant } from './context.js';

export type ModuleSection = 'command' | 'source' | 'sales' | 'deals' | 'performance' | 'finance' | 'system';

export interface ModuleDef {
  id: string;
  /** Đường dẫn ở web (giữ slug tiếng Việt như prototype). */
  route: string;
  label: string;
  icon: string;
  section: ModuleSection;
  order: number;
  /** Module lõi — không tắt được. */
  core?: boolean;
  /** Module phụ thuộc: tắt một module ⇒ module phụ thuộc vào nó cũng tắt. */
  requires?: string[];
  /** Hiện trong menu khi có ít nhất một quyền trong danh sách (rỗng = luôn hiện). */
  visible?: string[];
  mobileTab?: string;
  /** Service sở hữu dữ liệu chính của module. */
  service?: string;
}

export const MODULES: ModuleDef[] = [
  { id: 'dashboard', route: 'tong-quan', label: 'Tổng quan', icon: 'home', section: 'command', order: 1, core: true, mobileTab: 'Tổng quan', service: 'performance' },
  { id: 'process', route: 'quy-trinh', label: 'Quy trình vận hành', icon: 'flow', section: 'command', order: 2, core: true },
  { id: 'customers', route: 'khach-hang', label: 'Khách hàng', icon: 'users', section: 'source', order: 10, mobileTab: 'Khách', visible: ['customers.view'], service: 'crm' },
  { id: 'inventory', route: 'nguon-hang', label: 'Nguồn hàng', icon: 'building', section: 'source', order: 20, visible: ['inventory.view'], service: 'inventory' },
  { id: 'booking', route: 'chot-can', label: 'Chốt căn', icon: 'grid', section: 'sales', order: 30, mobileTab: 'Chốt căn', requires: ['inventory', 'customers', 'deals'], visible: ['booking.view'], service: 'booking' },
  { id: 'schedule', route: 'lich-hen', label: 'Lịch hẹn & việc', icon: 'calendar', section: 'sales', order: 40, visible: ['tasks.view'], service: 'crm' },
  { id: 'deals', route: 'giao-dich', label: 'Giao dịch & hợp đồng', icon: 'file', section: 'deals', order: 50, requires: ['inventory', 'customers'], visible: ['deals.view'], service: 'deal' },
  { id: 'attendance', route: 'cham-cong', label: 'Chấm công', icon: 'clock', section: 'performance', order: 60, mobileTab: 'Chấm công', visible: ['attendance.self', 'attendance.view'], service: 'attendance' },
  { id: 'sales', route: 'doanh-so', label: 'Doanh số & KPI', icon: 'chart', section: 'performance', order: 70, requires: ['deals', 'customers'], visible: ['sales.view'], service: 'performance' },
  { id: 'commission', route: 'hoa-hong', label: 'Hoa hồng & thu nhập', icon: 'wallet', section: 'finance', order: 80, requires: ['deals'], visible: ['commission.self', 'commission.view'], service: 'commission' },
  { id: 'hr', route: 'nhan-su', label: 'Nhân sự', icon: 'staff', section: 'system', order: 90, core: true, visible: ['hr.view'], service: 'hr' },
  { id: 'accounts', route: 'tai-khoan', label: 'Tài khoản & quyền', icon: 'shield', section: 'system', order: 92, core: true, visible: ['accounts.view'], service: 'identity' },
  { id: 'settings', route: 'cai-dat', label: 'Cài đặt', icon: 'settings', section: 'system', order: 94, core: true, visible: ['settings.manage'], service: 'identity' },
  { id: 'audit', route: 'nhat-ky', label: 'Nhật ký', icon: 'log', section: 'system', order: 96, core: true, visible: ['audit.view'], service: 'audit' },
];

export const SECTION_LABEL: Record<ModuleSection, string> = {
  command: 'Điều hành',
  source: 'Nguồn',
  sales: 'Bán hàng',
  deals: 'Giao dịch',
  performance: 'Hiệu suất',
  finance: 'Tài chính',
  system: 'Hệ thống',
};

export const moduleById = (id: string) => MODULES.find((m) => m.id === id);

/** Module bật hiệu lực: lõi hoặc cờ ≠ false, rồi loại lặp module thiếu phụ thuộc tới khi ổn định. */
export function enabledModules(flags: Record<string, boolean> = {}): string[] {
  let on = new Set(MODULES.filter((m) => m.core || flags[m.id] !== false).map((m) => m.id));
  for (;;) {
    const next = new Set([...on].filter((id) => (moduleById(id)?.requires ?? []).every((r) => on.has(r))));
    if (next.size === on.size) return MODULES.filter((m) => next.has(m.id)).map((m) => m.id);
    on = next;
  }
}

/** Module hiển thị cho người dùng = bật hiệu lực ∧ (visible rỗng ∨ có ít nhất một quyền trong visible). */
export function visibleModules(enabled: string[], grants: Record<string, Grant>): string[] {
  return enabled.filter((id) => {
    const v = moduleById(id)?.visible ?? [];
    return !v.length || v.some((p) => !!grants[p]);
  });
}

/** Module phải tắt theo khi tắt `id` (để hiển thị cảnh báo ở Cài đặt). */
export function dependentsOf(id: string): string[] {
  const after = enabledModules({ [id]: false });
  return enabledModules().filter((m) => m !== id && !after.includes(m));
}
